import { useEffect, useRef, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type {
  PublicProfile,
  Wish,
} from '@family-wishlist/shared';
import { api } from '@/lib/api';
import { WishCard } from '@/components/WishCard/WishCard';
import { Footer } from '@/components/Layout/Footer';
import { Gift } from 'lucide-react';
import { LanguageSwitcher } from '@/components/LanguageSwitcher';
import { useI18n } from '@/i18n/i18n';
import { useAuthStore } from '@/features/auth/authStore';
import { Button } from '@/components/ui/Button';

export function PublicProfilePage() {
  const { t } = useI18n();
  const user = useAuthStore((s) => s.user);
  const { slug = '' } = useParams<{ slug: string }>();
  const queryClient = useQueryClient();
  const [avatarFailed, setAvatarFailed] = useState(false);

  const profile = useQuery({
    queryKey: ['public-profile', slug],
    queryFn: () => api.get<PublicProfile>(`/api/public/${encodeURIComponent(slug)}`),
    retry: false,
    enabled: slug.length > 0,
  });

  const wishes = useQuery({
    queryKey: ['public-wishes', slug],
    queryFn: () => api.get<Wish[]>(`/api/public/${encodeURIComponent(slug)}/wishes`),
    enabled: slug.length > 0 && profile.isSuccess,
  });

  const markSeen = useMutation({
    mutationFn: (wishIds: string[]) =>
      api.post(`/api/public/${encodeURIComponent(slug)}/views`, { wishIds }),
  });

  const marked = useRef(false);
  useEffect(() => {
    if (!wishes.data || marked.current) return;
    const newIds = wishes.data.filter((w) => w.isNewForGuest).map((w) => w.id);
    if (newIds.length === 0) {
      marked.current = true;
      return;
    }
    const t = window.setTimeout(() => {
      markSeen.mutate(newIds, {
        onSuccess: () => {
          marked.current = true;
          void queryClient.invalidateQueries({ queryKey: ['public-wishes', slug] });
        },
      });
    }, 1500);
    return () => window.clearTimeout(t);
  }, [wishes.data, markSeen, queryClient, slug]);

  useEffect(() => {
    setAvatarFailed(false);
  }, [profile.data?.avatarUrl]);

  return (
    <div className="public-profile">
      <div className="public-profile__toolbar">
        {user ? (
          <Link to="/">
            <Button variant="secondary" size="sm">
              {t('public.backToMine')}
            </Button>
          </Link>
        ) : (
          <span />
        )}
        <LanguageSwitcher />
      </div>
      <main className="public-profile__main">
        {profile.isLoading && <div className="text-muted">{t('common.loading')}</div>}

        {profile.isError && (
          <div className="empty-state mx-auto max-w-lg bg-surface p-8">
            <h1 className="empty-state__title">{t('public.notFoundTitle')}</h1>
            <p className="empty-state__text mt-2">
              {t('public.notFoundText')}
            </p>
          </div>
        )}

        {profile.data && (
          <>
            <section className="public-profile__hero">
              <span className="public-profile__avatar">
                {profile.data.avatarUrl && !avatarFailed ? (
                  <img
                    src={profile.data.avatarUrl}
                    alt=""
                    className="public-profile__avatar-image"
                    onError={() => setAvatarFailed(true)}
                  />
                ) : (
                  <Gift className="h-6 w-6" />
                )}
              </span>
              <h1 className="public-profile__title">
                {t('public.wishlistTitle', { name: profile.data.displayName })}
              </h1>
              {profile.data.bio && (
                <p className="public-profile__bio">{profile.data.bio}</p>
              )}
            </section>

            {wishes.isLoading && <div className="text-muted">{t('public.loadingWishes')}</div>}

            {wishes.data && wishes.data.length === 0 && (
              <div className="empty-state mx-auto max-w-lg">
                <h2 className="empty-state__title">{t('public.emptyTitle')}</h2>
                <p className="empty-state__text mt-1">{t('public.emptyText')}</p>
              </div>
            )}

            {wishes.data && wishes.data.length > 0 && (
              <div className="grid gap-5 sm:grid-cols-2 lg:grid-cols-3">
                {wishes.data.map((wish) => (
                  <WishCard key={wish.id} wish={wish} view="guest" />
                ))}
              </div>
            )}
          </>
        )}
      </main>
      <Footer />
    </div>
  );
}
